import React from 'react'
import { useNavigate } from 'react-router-dom'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import PositionedTooltip from 'shared/components/PositionedTooltip/PositionedTooltip'
import CheckBox from 'shared/ui/CheckBox/CheckBox'
import Modal from 'shared/ui/Modal/Modal'
import { SelectionBar } from 'shared/components/SelectionBar/SelectionBar'
import { FiltersPanel } from 'widgets/FiltersPanel/FiltersPanel'
import FiltersBar from 'pages/ApplicationsPage/components/FiltersBar/FiltersBar'
import { SortableHeaderCell } from 'shared/components/DataTable/components/SortableHeaderCell/SortableHeaderCell'
import { useIsMobileLg } from 'shared/hooks/useBreakpoint'
import { navigateToStudentProfile } from 'pages/Dashboard/components/TodaysPrioritySection/utils/priorityCardUi'
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg'
import { ReactComponent as ListIcon } from 'assets/icons/list.svg'
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg'
import { ReactComponent as MagicIcon } from 'assets/icons/magic.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash-2.svg'
import FiltersContent from './FiltersContent'
import AssignModal from './AssignModal'
import s from './styles/Students.module.scss'

const mkSortOptions = (key, labels) => [
	{ id: `${key}_asc`, key, dir: 'asc', label: labels?.asc || 'A → Z' },
	{ id: `${key}_desc`, key, dir: 'desc', label: labels?.desc || 'Z → A' },
]

const SORT_OPTIONS = {
	student: mkSortOptions('student'),
	grade: mkSortOptions('grade', { asc: 'Low → High', desc: 'High → Low' }),
	plan: mkSortOptions('plan'),
	counselor: mkSortOptions('counselor'),
	assignments: mkSortOptions('assignments', { asc: 'Low → High', desc: 'High → Low' }),
}

const COLUMNS = [
	{ id: 'student', label: 'Student' },
	{ id: 'grade', label: 'Grade' },
	{ id: 'plan', label: 'Kandor plan' },
	{ id: 'counselor', label: 'Counselor' },
	{ id: 'assignments', label: 'Assignments' },
]

export const Students = ({
	students,
	counselors,
	vm,
}) => {
	const {
		counts,
		filtered,
		selected,
		selectedIds,
		selectedStudents,
		hasSelection,
		allSelected,
		cohort,
		setCohort,
		sortKey,
		sortDir,
		filterChips,
		isFilterOpen,
		draftFilters,
		chooserOpen,
		setChooserOpen,
		hoveredCounselorId,
		setHoveredCounselorId,
		counselorLoads,
		isAssigning,
		toggleOne,
		toggleAll,
		clearSelection,
		setSort,
		removeFilterChip,
		clearAllPanelFilters,
		onChangeFilterKey,
		applyFilters,
		openFilter,
		closeFilter,
		assignSelected,
		removeFromSelection,
		requestRemoveStudent,
		requestRemoveSelected,
		deleteConfirm,
		isDeleting,
		cancelDeleteConfirm,
		confirmDelete,
		actionsMenuId,
		statusConfirm,
		toggleActionsMenu,
		requestToggleStudentDisabled,
		cancelStatusConfirm,
		confirmStatusChange,
	} = vm

	const navigate = useNavigate()
	const isMobileTable = useIsMobileLg()
	const studentAvatarSize = isMobileTable ? 28 : 34

	const openStudentProfile = studentId => {
		navigateToStudentProfile(navigate, studentId)
	}

	const selectionActions = [
		{
			id: 'assign',
			label: 'Assign counselor',
			icon: <PeopleIcon />,
			onClick: () => setChooserOpen(true),
		},
		{
			id: 'smart',
			label: 'Smart assign',
			icon: <MagicIcon />,
			disabled: true,
			title: 'Coming soon',
		},
		{
			id: 'delete',
			label: 'Delete students',
			icon: <TrashIcon />,
			onClick: requestRemoveSelected,
		},
	]

	return (
		<div className={s.wrap}>
			<div className={s.pills}>
				<div className={s.roadmap}>
					<button
						type="button"
						className={`${s.roadBtn} ${cohort === 'all' ? s.roadActive : ''}`.trim()}
						onClick={() => setCohort('all')}
						aria-pressed={cohort === 'all'}
					>
						<span className={s.roadIcon} aria-hidden="true">
							<ListIcon />
						</span>
						<span className={s.roadLabel}>All students</span>
						<span className={s.roadCount}>{counts.all}</span>
					</button>
					<button
						type="button"
						className={`${s.roadBtn} ${s.roadUnassigned} ${cohort === 'unassigned' ? s.roadActive : ''}`.trim()}
						onClick={() => setCohort(cohort === 'unassigned' ? 'all' : 'unassigned')}
						aria-pressed={cohort === 'unassigned'}
					>
						<span className={s.roadDot} aria-hidden="true" />
						<span className={s.roadLabel}>Unassigned</span>
						<span className={s.roadCount}>{counts.unassigned}</span>
					</button>
					<button
						type="button"
						className={`${s.roadBtn} ${s.roadAssigned} ${cohort === 'assigned' ? s.roadActive : ''}`.trim()}
						onClick={() => setCohort(cohort === 'assigned' ? 'all' : 'assigned')}
						aria-pressed={cohort === 'assigned'}
					>
						<span className={s.roadDot} aria-hidden="true" />
						<span className={s.roadLabel}>Assigned</span>
						<span className={s.roadCount}>{counts.assigned}</span>
					</button>
				</div>
				<FiltersBar
					chips={filterChips}
					onRemoveChip={removeFilterChip}
					onClearAll={clearAllPanelFilters}
				/>
			</div>

			<SelectionBar
				isVisible={hasSelection}
				isOffset={isFilterOpen}
				text={
					selectedIds.length === 1
						? '1 student selected'
						: `${selectedIds.length} students selected`
				}
				onClear={clearSelection}
				onClose={clearSelection}
				actions={selectionActions}
				className={s.banner}
			/>

			<div className={`${s.body} ${isFilterOpen ? s.filterOpen : ''}`.trim()}>
				{!isFilterOpen ? (
					<div className={s.tableToolbar}>
						<button
							type="button"
							className={s.filtersMobileBtn}
							onClick={openFilter}
							aria-label="Open filters"
						>
							<FilterIcon aria-hidden />
							<span>Filters</span>
						</button>
					</div>
				) : null}

				<div className={s.scroll}>
					<table className={s.table}>
						<thead>
							<tr>
								<th className={`${s.check} ${s.stickyCheck}`}>
									<CheckBox
										isSelected={allSelected}
										onToggle={toggleAll}
										variant="checkmark"
										className={s.checkBox}
										classNameLabel={s.checkLabel}
									/>
								</th>
								{COLUMNS.map(col => (
									<th
										key={col.id}
										className={col.id === 'student' ? s.stickyStudent : undefined}
									>
										<SortableHeaderCell
											className={s.sortHead}
											labelClassName={s.sortLabel}
											label={col.label}
											isActive={sortKey === col.id}
											dir={sortDir}
											options={SORT_OPTIONS[col.id]}
											onSelect={opt =>
												setSort(opt.key, opt.dir, `${col.label}: ${opt.label}`)
											}
										/>
									</th>
								))}
								<th className={s.filterHead}>
									{!isFilterOpen && (
										<button
											type="button"
											className={s.filterBtn}
											onClick={openFilter}
											aria-label="Open filters"
										>
											<FilterIcon aria-hidden />
										</button>
									)}
								</th>
							</tr>
						</thead>
						<tbody>
							{filtered.map(row => (
								<tr key={row.id} className={`${selected.has(row.id) ? s.selected : ''} ${row.disabled ? s.disabledRow : ''}`.trim()}>
									<td className={`${s.check} ${s.stickyCheck}`}>
										<CheckBox
											isSelected={selected.has(row.id)}
											onToggle={() => toggleOne(row.id)}
											disabled={row.disabled}
											variant="checkmark"
											className={s.checkBox}
											classNameLabel={s.checkLabel}
										/>
									</td>
									<td className={s.stickyStudent}>
										<PositionedTooltip
											content="Open student profile"
											className={s.studentTooltip}
										>
											<button
												type="button"
												className={s.person}
												onClick={() => openStudentProfile(row.id)}
												aria-label={`Open ${row.name} profile`}
											>
												<InitialAvatar
													name={row.name}
													avatarKey={row.avatarKey}
													size={studentAvatarSize}
													studentId={row.id}
												/>
												<span className={s.studentIdentity}>
													<strong>{row.name}</strong>
													{row.disabled ? (
														<small className={s.disabledBadge}>
															Disabled
														</small>
													) : null}
												</span>
											</button>
										</PositionedTooltip>
									</td>
									<td className={s.grade}>Grade {row.grade}</td>
									<td>
										<span className={row.plan === 'Premium' ? s.planPremium : s.planEssential}>
											{row.plan}
										</span>
									</td>
									<td>
										{row.counselor === 'Unassigned' ? (
											<span className={s.unassigned}>Unassigned</span>
										) : (
											<span className={s.counselor}>
												<InitialAvatar name={row.counselor} size={22} />
												{row.counselor}
											</span>
										)}
									</td>
									<td>
										{row.assignments ? (
											<span className={s.assignmentCount}>
												<strong>{row.assignments}</strong> assigned
											</span>
										) : (
											<span className={s.noAssignments}>None</span>
										)}
									</td>
									<td className={s.actions} data-student-actions>
										{row.disabled ? (
											<div className={s.disabledActions}>
												<PositionedTooltip content="Enable account"><button type="button" className={s.enableButton} onClick={() => requestToggleStudentDisabled(row)} aria-label={`Enable ${row.name}`}><span className="material-icons-outlined" aria-hidden="true">play_circle</span></button></PositionedTooltip>
												<PositionedTooltip content="Delete student"><button type="button" className={s.deleteButton} onClick={() => requestRemoveStudent(row.id)} aria-label={`Delete ${row.name}`}><TrashIcon aria-hidden /></button></PositionedTooltip>
											</div>
										) : <>
										<button
											type="button"
											className={s.moreButton}
											onClick={() => toggleActionsMenu(row.id)}
											aria-label={`Actions for ${row.name}`}
											aria-expanded={actionsMenuId === row.id}
										>
											<span className="material-icons-outlined" aria-hidden="true">more_vert</span>
										</button>
										{actionsMenuId === row.id ? <div className={s.actionMenu} role="menu">
											<button type="button" role="menuitem" onClick={() => requestToggleStudentDisabled(row)}><span className="material-icons-outlined" aria-hidden="true">block</span>Disable account</button>
											<button type="button" role="menuitem" className={s.removeAction} onClick={() => requestRemoveStudent(row.id)}><TrashIcon aria-hidden />Delete student</button>
										</div> : null}
										</>}
									</td>
								</tr>
							))}
							{!filtered.length && (
								<tr>
									<td colSpan="7" className={s.empty}>
										No students match these filters.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<FiltersPanel isOpen={isFilterOpen} onClose={closeFilter}>
					<FiltersContent
						students={students}
						counselors={counselors}
						selected={draftFilters}
						onChangeKey={onChangeFilterKey}
						onApply={applyFilters}
						onClose={closeFilter}
					/>
				</FiltersPanel>
			</div>

			<AssignModal
				open={chooserOpen}
				selectedStudents={selectedStudents}
				counselors={counselorLoads}
				hoveredId={hoveredCounselorId}
				onHover={setHoveredCounselorId}
				onClose={() => setChooserOpen(false)}
				onRemoveFromSelection={removeFromSelection}
				onConfirmAssign={assignSelected}
				isAssigning={isAssigning}
			/>

			<Modal
				isOpen={Boolean(deleteConfirm)}
				onClose={cancelDeleteConfirm}
				title={deleteConfirm?.title || 'Delete student?'}
				subtitle="This will permanently delete the selected student record."
				ariaLabel="Delete student confirmation"
				width={480}
				compact
				footerButtons={[
					{
						id: 'cancel',
						text: 'Cancel',
						variant: 'secondary',
						disabled: isDeleting,
						onClick: cancelDeleteConfirm,
					},
					{
						id: 'remove',
						text: isDeleting ? 'Deleting...' : 'Delete student',
						variant: 'primary',
						disabled: isDeleting,
						onClick: confirmDelete,
					},
				]}
			/>

			<Modal
				isOpen={Boolean(statusConfirm)}
				onClose={cancelStatusConfirm}
				title={statusConfirm?.disabled ? `Disable ${statusConfirm?.studentName}?` : `Enable ${statusConfirm?.studentName}?`}
				subtitle={statusConfirm?.disabled ? 'The student will no longer be able to use their Kandor account.' : 'The student will regain access to their Kandor account.'}
				ariaLabel="Change student account status"
				width={480}
				compact
				footerButtons={[
					{ id: 'cancel', text: 'Cancel', variant: 'secondary', onClick: cancelStatusConfirm },
					{ id: 'confirm', text: statusConfirm?.disabled ? 'Disable account' : 'Enable account', variant: 'primary', onClick: confirmStatusChange },
				]}
			/>
		</div>
	)
}

export default Students
